import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { Placeholders } from '@ge/models/constants';

import { ReportWidgetTable } from '../../tables/report-widget-table';

import {
  InternalInspectionDamagesColumns,
  internalInspectionDamagesColumnsDefs,
} from './internal-inspection-damages-cols';

dayjs.extend(isoWeek);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const InternalInspectionDamagesTableWidget = ({
  name,
  columns,
  data = [],
  isEditing,
  isInteractive,
  onRowSelectionChange,
}) => {
  const { t, ready } = useTranslation(['reporting.widgets']);
  // initialize all rows being selected
  const [rowsSelected] = useState(() => data.map(({ id }) => id));

  useEffect(() => {
    onRowSelectionChange(rowsSelected);
  }, [onRowSelectionChange, rowsSelected]);

  const cellValueMapFn = useCallback(
    (cellValue) => {
      return {
        [InternalInspectionDamagesColumns.ASSET]: cellValue.asset
          ? cellValue.asset
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.MODEL]: cellValue.model
          ? cellValue.model
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.SERIAL_NUMBER]: cellValue.serialNumber
          ? cellValue.serialNumber
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.COMPONENT]: cellValue.component
          ? cellValue.component
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.LOCATION]: cellValue.location
          ? cellValue.location
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.DAMAGE]: cellValue.damage
          ? cellValue.damage
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.PAC]: cellValue.pacNumber
          ? cellValue.pacNumber
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.PART]: cellValue.part
          ? cellValue.part
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.CATEGORY]: cellValue.category
          ? cellValue.category
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.MANUFACTURER]: cellValue.manufacturer
          ? cellValue.manufacturer
          : Placeholders.DASH,
        [InternalInspectionDamagesColumns.P50]: cellValue.p50 ? cellValue.p50 : Placeholders.DASH,
        [InternalInspectionDamagesColumns.P90]: cellValue.p90 ? cellValue.p90 : Placeholders.DASH,
      };
    },
    [t],
  );

  // Table factories
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: internalInspectionDamagesColumnsDefs,
    cellValueMapFn,
  });

  if (!ready) {
    return <Loader />;
  }

  return (
    <Container>
      <ReportWidgetTable
        key={name}
        compressed
        noTitles
        scrollable={isInteractive}
        transparent
        columnGroupFactory={columnGroupFactory}
        columnFactory={columnFactory}
        cellFactory={cellFactory}
        columns={columns}
        values={data}
        onValueSelect={() => null}
        rowsSelected={rowsSelected}
        rowKeyProperty={'id'}
        isEditing={isEditing}
        variableHeight={true}
        vAlign={'middle'}
      />
    </Container>
  );
};

InternalInspectionDamagesTableWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  isEditing: PropTypes.bool,
  isInteractive: PropTypes.bool,
  onRowSelectionChange: PropTypes.func.isRequired,
};
